import React, { useState, useEffect } from 'react';
import PageLayout from '../components/PageLayout';
import { businessService, categoryService, areaService } from '../services';
import './MyBusinesses.css';

const MyBusinesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    subcategory_id: '',
    area_id: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    opening_hours: '',
    image_url: '',
    gallery_images: [],
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMyBusinesses();
    fetchCategories();
    fetchAreas();
  }, []);

  const fetchMyBusinesses = async () => {
    try {
      setLoading(true);
      const data = await businessService.getMyBusinesses();
      setBusinesses(data.businesses);
      setError('');
    } catch (err) {
      setError('Failed to fetch your businesses');
      console.error('Error fetching businesses:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await categoryService.getCategories();
      setCategories(data.categories);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchAreas = async () => {
    try {
      const data = await areaService.getAreas();
      setAreas(data.areas);
    } catch (err) {
      console.error('Error fetching areas:', err);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await fetch(`https://businessdiary-api.tejasborade9594.workers.dev/api/subcategories/category/${categoryId}`);
      const data = await response.json();
      setSubcategories(data.subcategories || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubcategories([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If category changes, fetch subcategories and reset subcategory
    if (name === 'category_id') {
      setFormData({
        ...formData,
        [name]: value,
        subcategory_id: '',
      });
      if (value) {
        fetchSubcategories(value);
      } else {
        setSubcategories([]);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const openCreateModal = () => {
    setEditingBusiness(null);
    setFormData({
      name: '',
      description: '',
      category_id: '',
      subcategory_id: '',
      area_id: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      opening_hours: '',
      image_url: '',
      gallery_images: [],
    });
    setSubcategories([]);
    setShowModal(true);
    setMessage('');
    setError('');
  };

  const openEditModal = (business) => {
    setEditingBusiness(business);
    setFormData({
      name: business.name,
      description: business.description,
      category_id: business.category_id,
      subcategory_id: business.subcategory_id || '',
      area_id: business.area_id,
      address: business.address,
      phone: business.phone || '',
      email: business.email || '',
      website: business.website || '',
      opening_hours: business.opening_hours || '',
      image_url: business.image_url || '',
      gallery_images: business.gallery_images || [],
    });
    // Fetch subcategories if category exists
    if (business.category_id) {
      fetchSubcategories(business.category_id);
    }
    setShowModal(true);
    setMessage('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    // Validate required fields
    if (!formData.name || !formData.category_id || !formData.area_id || !formData.address) {
      setError('Please fill all required fields');
      return;
    }

    try {
      if (editingBusiness) {
        await businessService.updateBusiness(editingBusiness.id, formData);
        setMessage('Business updated successfully!');
      } else {
        await businessService.createBusiness(formData);
        setMessage('Business created successfully!');
      }
      setTimeout(() => {
        setShowModal(false);
        fetchMyBusinesses();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (businessId, businessName) => {
    if (!window.confirm(`Are you sure you want to delete "${businessName}"?`)) {
      return;
    }

    try {
      await businessService.deleteBusiness(businessId);
      setMessage('Business deleted successfully!');
      fetchMyBusinesses();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete business');
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <PageLayout>
      <div className="container">
        <div className="page-header">
          <h1>My Businesses</h1>
          <button onClick={openCreateModal} className="btn btn-primary">
            + Add New Business
          </button>
        </div>

        {/* Floating Action Button */}
        <button
          onClick={openCreateModal}
          className="fab"
          title="Add New Business"
          aria-label="Add New Business"
        >
          <span className="fab-icon">+</span>
        </button>

        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {loading ? (
          <div className="loading">Loading your businesses...</div>
        ) : businesses.length === 0 ? (
          <div className="card empty-state">
            <div className="empty-icon">🏪</div>
            <h2>No businesses yet</h2>
            <p>Start by adding your first business listing</p>
            <button onClick={openCreateModal} className="btn btn-primary">
              Add Your First Business
            </button>
          </div>
        ) : (
          <div className="businesses-grid">
            {businesses.map((business) => (
              <div key={business.id} className="business-card">
                {business.image_url && (
                  <div className="business-image-header">
                    <img 
                      src={business.image_url} 
                      alt={business.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                <div className="business-header">
                  <h3>{business.name}</h3>
                  {business.is_verified && (
                    <span className="verified-badge">✓ Verified</span>
                  )}
                </div>

                <div className="business-info">
                  <div className="info-row">
                    <span className="label">Category:</span>
                    <span className="badge badge-category">
                      {business.category?.name || 'N/A'}
                    </span>
                  </div>

                  <div className="info-row">
                    <span className="label">Location:</span>
                    <span>
                      {business.area?.name}, {business.area?.city}
                    </span>
                  </div>

                  <div className="info-row">
                    <span className="label">Address:</span>
                    <span>{business.address}</span>
                  </div>

                  {business.phone && (
                    <div className="info-row">
                      <span className="label">Phone:</span>
                      <span>{business.phone}</span>
                    </div>
                  )}

                  {business.email && (
                    <div className="info-row">
                      <span className="label">Email:</span>
                      <span>{business.email}</span>
                    </div>
                  )}

                  {business.rating > 0 && (
                    <div className="info-row">
                      <span className="label">Rating:</span>
                      <span className="rating">
                        {'⭐'.repeat(Math.round(business.rating))} ({business.rating.toFixed(1)})
                      </span>
                    </div>
                  )}
                </div>

                <div className="business-description">
                  <p>{business.description}</p>
                </div>

                <div className="business-actions">
                  <button
                    onClick={() => openEditModal(business)}
                    className="btn btn-secondary btn-sm"
                    title="Edit business details"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDelete(business.id, business.name)}
                    className="btn btn-danger btn-sm"
                    title="Delete business"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div
            className="modal-overlay modal-fade-in"
            onClick={() => setShowModal(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="business-modal-title"
          >
            <div className="modal-content modal-slide-up" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-header-content">
                  <div className="modal-icon">
                    {editingBusiness ? '✏️' : '🏪'}
                  </div>
                  <h2 id="business-modal-title">
                    {editingBusiness ? 'Edit Business' : 'Add New Business'}
                  </h2>
                </div>
                <button className="modal-close" onClick={() => setShowModal(false)} aria-label="Close modal">
                  &times;
                </button>
              </div>

              {message && <div className="alert alert-success">{message}</div>}
              {error && <div className="alert alert-error">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Business Name *</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="category_id"
                    className="form-control"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.category_id && subcategories.length > 0 && (
                  <div className="form-group">
                    <label>Subcategory</label>
                    <select
                      name="subcategory_id"
                      className="form-control"
                      value={formData.subcategory_id}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Subcategory (Optional)</option>
                      {subcategories.map((subcat) => (
                        <option key={subcat.id} value={subcat.id}>
                          {subcat.icon} {subcat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Area *</label>
                  <select
                    name="area_id"
                    className="form-control"
                    value={formData.area_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Area</option>
                    {areas.map((area) => (
                      <option key={area.id} value={area.id}>
                        {area.name}, {area.city}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Address *</label>
                  <textarea
                    name="address"
                    className="form-control"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="3"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    name="description"
                    className="form-control"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="4"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      className="form-control"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Website</label>
                  <input
                    type="url"
                    name="website"
                    className="form-control"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://..."
                  />
                </div>

                <div className="form-group">
                  <label>Opening Hours</label>
                  <input
                    type="text"
                    name="opening_hours"
                    className="form-control"
                    value={formData.opening_hours}
                    onChange={handleInputChange}
                    placeholder="e.g., Mon-Fri: 9AM-6PM"
                  />
                </div>

                <div className="form-group">
                  <label>Business Image URL</label>
                  <input
                    type="url"
                    name="image_url"
                    className="form-control"
                    value={formData.image_url}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image.jpg"
                  />
                  <small style={{ color: '#6c757d' }}>
                    Enter image URL (e.g., from Unsplash, Google Images, or your website)
                  </small>
                  {formData.image_url && (
                    <div style={{ marginTop: '10px' }}>
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '200px', 
                          borderRadius: '8px',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingBusiness ? 'Update Business' : 'Create Business'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default MyBusinesses;
